import { useInfiniteQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import Api from "../../api/generalApi/Api";
 import i18n from "../../i18n";
import NoResult from "./NoResult";

type InfiniteScrollProps = {
  endpoint: string;
  getAllParams?: any;
  renderSkeleton: JSX.Element;
  getData?: (data: any) => void;
  Count?: number;
  noDataElement?: JSX.Element;
  skeletonCount?: number;
  extraDataKey?: string;
  customQueryKey?: string;
  enabled?: boolean;
};

const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  endpoint,
  getAllParams = {},
  renderSkeleton,
  getData,
  Count = 10,
  noDataElement,
  skeletonCount,
  extraDataKey,
  customQueryKey,
  enabled,
}) => {
  const defaultPaginationParams = {
    Start: 0,
    Count: Count,
    currentPage: 0,
  };

  const [paginationParams, setPaginationParams] = useState<any>(
    defaultPaginationParams,
  );

  const removeUndefinedParams = (obj: any) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([key, value]) => value !== undefined && value !== null,
      ),
    );
  };

  const { ref, inView } = useInView({
    threshold: 0.1,
  });

  const fetchData = async ({ signal }: any, filter?: any[]): Promise<any> => {
    return await Api<any, any>({
      method: "GET",
      endpoint: endpoint,
      signal,
      filter: filter,
    });
  };
  const {
    data,
    isSuccess,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetched,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [customQueryKey ?? endpoint, getAllParams, i18n.language],
    queryFn: ({ pageParam = 0 }) =>
      fetchData({ pageParam }, [
        {
          ...removeUndefinedParams(getAllParams),
          Count: Count ?? 10,
          Start: paginationParams?.Count * pageParam,
        },
      ]),
    initialPageParam: 0,
    enabled: enabled,
    getNextPageParam: (lastPage: any, pages, allPages) => {
      const maxPage = Math.ceil(
        lastPage?.TotalCount / (paginationParams?.Count ?? 1),
      );
      const nextPage = (allPages ?? 0) + 1;
      return nextPage < maxPage ? nextPage : undefined;
    },
  });

  useEffect(() => {
    if (inView) {
      setPaginationParams((prev: any) => {
        return {
          ...prev,
          Start: prev?.Start + prev?.Count,
          currentPage: prev?.currentPage + 1,
        };
      });
      fetchNextPage();
    }
  }, [inView, isSuccess, isFetched, data]);

  const itemsData = data?.pages
    ?.flatMap((page: any) =>
      extraDataKey ? page?.Data?.[extraDataKey] : page?.Data,
    )
    ?.filter(Boolean);

  useEffect(() => {
    getData && getData(itemsData);
  }, [isSuccess, isFetchingNextPage, data?.pages]);

  const loadingElement = Array.from({
    length: skeletonCount ?? Count / 2,
  })?.map((_, index) => (
    <React.Fragment key={index}>{renderSkeleton}</React.Fragment>
  ));

  return (
    <>
      {isLoading
        ? loadingElement
        : !itemsData ||
          (itemsData?.length == 0 && (
            <div className="col-span-full row-span-full flex h-full w-full items-center justify-center">
              <div className="flex h-fit grow flex-col items-center gap-2">
                {noDataElement ?? <NoResult isWithLogo />}
              </div>
            </div>
          ))}

      {(isFetchingNextPage || hasNextPage) && (
        <>
          <div
            ref={ref}
            className="loading-skeleton invisible col-span-full h-1 w-full"
          />

          {loadingElement}
        </>
      )}
    </>
  );
};

export default InfiniteScroll;
